import { Box, Typography } from '@mui/material';
import React from 'react';
import {
  CommunicationDirection,
  MessageLog,
  MessagePartyType,
  TWILIO_ERROR_STATUSES,
  TrackingNumber,
  TrackingNumberWithAgents,
} from '../../../../interfaces/appointments';
import { formatDateString } from '../../../../util/dateFormat';
import { resolveMessageLabel } from '../utils';

interface ChatMessageProps {
  trackingNumbers: TrackingNumberWithAgents[];
  message: MessageLog & {
    lastOutboundMessageSent?: MessageLog | null;
  };
  date: string;
  isSelected: boolean;
  currentTrackingNumber: TrackingNumber | null;
  renderResolutionChip: (date: string, message: MessageLog) => React.ReactNode;
  messageRef: React.RefObject<HTMLDivElement>;
}

const ChatMessage = ({
  trackingNumbers,
  message,
  date,
  isSelected,
  renderResolutionChip,
  currentTrackingNumber,
  messageRef,
}: ChatMessageProps) => {
  const isInbound = message.direction === CommunicationDirection.INBOUND;
  const systemPhone = trackingNumbers.find(
    (tn) => tn.trackingNumber === message.to || tn.trackingNumber === message.from,
  );
  const memberPhoneNumber = message.fromType === MessagePartyType.USER ? message.from : message.to;
  const lastOutbound = message.lastOutboundMessageSent;

  return (
    <Box
      ref={isSelected ? messageRef : null}
      reference-message-id={`${currentTrackingNumber?.trackingNumber}-${message.id}`}
      className={`chat-message ${isSelected ? 'selected-message' : ''} ${
        isInbound ? 'inbound-message' : 'outbound-message'
      } ${TWILIO_ERROR_STATUSES.includes(message.deliveryStatus) ? 'error-message' : ''}`}>
      <Box
        display='flex'
        flexDirection='row'
        alignItems='center'
        gap={1}
        className='message-sender-container'>
        {isInbound ? (
          <>
            <Typography variant='subtitle2' className='message-sender'>
              {message.coreMemberFullname || 'Unknown'}
            </Typography>
            <Typography variant='subtitle2' className='message-sender-phone'>
              {memberPhoneNumber || 'Unknown'}
            </Typography>
          </>
        ) : (
          <>
            <Typography variant='subtitle2' className='message-sender'>
              {resolveMessageLabel(message, systemPhone)}
            </Typography>
            <Typography variant='subtitle2' className='message-sender-phone'>
              {systemPhone?.trackingNumber || 'Unknown'}
            </Typography>
          </>
        )}
        {renderResolutionChip(date, message)}
      </Box>

      <Box className='message-bubble'>
        {lastOutbound && (
          <Box className='last-outbound-preview'>
            <Box display='flex' gap={1}>
              <Typography variant='subtitle2' className='message-sender-outbound-preview'>
                {resolveMessageLabel(message, systemPhone)}
              </Typography>
              <Typography variant='subtitle2' className='message-sender-outbound-preview'>
                {systemPhone?.trackingNumber || 'Unknown'}
              </Typography>
            </Box>
            <Typography variant='body2' noWrap>
              {lastOutbound.content.length > 50
                ? `${lastOutbound.content.substring(0, 50)}...`
                : lastOutbound.content}
            </Typography>
          </Box>
        )}
        <Box className='message-bubble-response'>
          <Typography variant='body1'>{message.content}</Typography>
          <Typography variant='caption' className='message-time'>
            {formatDateString(message.createdAt, 'HH:mm A')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatMessage;
